import { IPosition, TPopupHorizontal, TPopupVertical, TPosition } from '../interface/position'

import { getDocumentElement } from './getDocumentElement'

export interface ExtendedDomRect extends DOMRect {
  horizontalCenter: number
  verticalCenter: number
}

export interface IPopupData {
  startPosition: {
    horizontal: TPopupHorizontal
    vertical: TPopupVertical
  }
  direction: {
    horizontal: TPopupHorizontal
    vertical: TPopupVertical
  }
  offset: {
    horizontal: number
    vertical: number
  }
  position: TPosition
  targetCoords: ExtendedDomRect
  popupCoords: DOMRect
}

type TDirection = 'vertical' | 'horizontal'

export const REVERTED_POSITION = {
  [TPopupVertical.Top]: TPopupVertical.Bottom,
  [TPopupVertical.Bottom]: TPopupVertical.Top,
  [TPopupVertical.Center]: TPopupVertical.Center,
  [TPopupHorizontal.Right]: TPopupHorizontal.Left,
  [TPopupHorizontal.Left]: TPopupHorizontal.Right,
}

export const calculatePosition = (popupData: IPopupData, container?: HTMLElement) => {
  const horizontalPosition = calculateWithOverflow({
    popupData,
    direction: 'horizontal',
    container,
  })
  const verticalPosition = calculateWithOverflow({
    popupData,
    direction: 'vertical',
    container,
  })
  return { ...horizontalPosition, ...verticalPosition }
}

const getContainerOffset = (direction: TDirection, container?: HTMLElement) => {
  if (!container) {
    return 0
  }
  const rect = container.getBoundingClientRect?.()
  if (!rect) {
    return 0
  }
  return direction === 'horizontal' ? rect.left : rect.top
}

const calculateWithOverflow = ({
  popupData,
  direction,
  container,
}: {
  popupData: IPopupData
  direction: TDirection
  container?: HTMLElement
}) => {
  let result = getPosition({ popupData, direction, container })
  const overflow = getOverflow({ position: result, popupData, direction, container })
  if (overflow > 0) {
    invertPosition(popupData, direction)
    const invertResult = getPosition({ popupData, direction, container })
    const invertOverflow = getOverflow({ position: invertResult, popupData, direction, container })
    if (invertOverflow < 0 || overflow > invertOverflow) {
      result = invertResult
    } else if (overflow < invertOverflow) {
      invertPosition(popupData, direction)
    }

    result = restrictContainer({ position: result, popupData, container })
  }
  return prepareResult(result, popupData)
}

const restrictContainer = ({
  position,
  popupData,
  container,
}: {
  position: IPosition
  popupData: IPopupData
  container?: HTMLElement
}) => {
  if (position.bottom) {
    const diff =
      position.bottom +
      popupData.popupCoords.height -
      getWindowSizeByDirection('vertical', container)
    if (diff > 0) {
      position['height'] = popupData.popupCoords.height - diff
    }
  }

  if (position.top) {
    const diff =
      position.top + popupData.popupCoords.height - getWindowSizeByDirection('vertical', container)
    if (diff > 0) {
      position['height'] = popupData.popupCoords.height - diff
    }
  }

  return position
}

const prepareResult = (position: IPosition, popupData: IPopupData) => {
  const keys = Object.keys(position)

  const getDirection = (key: string) => {
    if (key === 'right' || key === 'left') {
      return 'horizontal'
    }
    return 'vertical'
  }

  for (const key of keys) {
    if (typeof position[key] === 'number') {
      const direction = getDirection(key)
      const offset = popupData.offset[direction]
      if (position[key] < 0 && isNegativeCoordinateInWindow(position[key], offset)) {
        position[key] = 0
      }
    }
  }
  return position
}

const isNegativeCoordinateInWindow = (value: number, offset: number) => {
  return Math.abs(value) <= Math.abs(offset)
}

const getOverflow = ({
  position,
  popupData,
  direction,
  container,
}: {
  position: IPosition
  popupData: IPopupData
  direction: TDirection
  container?: HTMLElement
}) => {
  const coordinate = calculateCoordinate(popupData, direction)
  const value = position[coordinate] || 0

  if (value < 0) {
    if (isNegativeCoordinateInWindow(value, popupData.offset[direction])) {
      return -value
    }
  }

  const popupSizeProperty = direction === 'horizontal' ? 'width' : 'height'
  const popupSize = popupData.popupCoords[popupSizeProperty]

  // When a container is provided (shadow DOM / mount point), `position` is in container coordinates.
  // For "flip on viewport edge" we must compute overflow in viewport coordinates.
  if (container) {
    const containerRect = container.getBoundingClientRect()
    const viewportSize =
      direction === 'horizontal'
        ? document.documentElement.clientWidth
        : document.documentElement.clientHeight

    let startInViewport = 0
    if (direction === 'vertical') {
      if (coordinate === TPopupVertical.Top) {
        startInViewport = containerRect.top + value
      } else if (coordinate === TPopupVertical.Bottom) {
        startInViewport = containerRect.bottom - value - popupSize
      } else {
        // center -> treat as top for overflow math
        startInViewport = containerRect.top + value - popupSize / 2
      }
    } else if (direction === 'horizontal') {
      if (coordinate === TPopupHorizontal.Left) {
        startInViewport = containerRect.left + value
      } else if (coordinate === TPopupHorizontal.Right) {
        startInViewport = containerRect.right - value - popupSize
      } else {
        startInViewport = containerRect.left + value - popupSize / 2
      }
    }

    return startInViewport + popupSize - viewportSize
  }

  const windowSize = getWindowSizeByDirection(direction)
  const scroll = getScroll({ direction, position: popupData.position, container })
  return value + popupSize - windowSize - scroll
}

const getPosition = ({
  popupData,
  direction,
  container,
}: {
  popupData: IPopupData
  direction: TDirection
  container?: HTMLElement
}) => {
  const position = {}
  const popupCoordinate = calculateCoordinate(popupData, direction)
  let coordinate: string = popupCoordinate
  const isRevertPopupDirection = isRevertDirection(popupCoordinate)
  const startPosition = getStartPositionValue({ popupData, direction, container })
  const containerOffset = getContainerOffset(direction, container)
  if (coordinate === 'center') {
    if (direction === 'horizontal') {
      coordinate = 'left'
      position['left'] = startPosition - popupData.popupCoords.width / 2
    }
    if (direction === 'vertical') {
      coordinate = 'top'
      position['top'] = startPosition - popupData.popupCoords.height / 2
    }
  } else {
    if (isRevertPopupDirection) {
      const windowSize = getWindowSizeByDirection(direction, container)
      const revertStartPosition = windowSize - startPosition
      position[coordinate] = revertStartPosition + containerOffset
    } else {
      position[coordinate] = startPosition - containerOffset
    }
  }

  position[coordinate] += popupData.offset[direction]
  return position
}

const invertPosition = (popupData: IPopupData, direction: TDirection) => {
  // @ts-ignore
  popupData.startPosition[direction] = REVERTED_POSITION[popupData.startPosition[direction]]
  // @ts-ignore
  popupData.direction[direction] = REVERTED_POSITION[popupData.direction[direction]]
}

const calculateCoordinate = (popupData: IPopupData, direction: TDirection) => {
  return REVERTED_POSITION[popupData.direction[direction]]
}

const getWindowSizeByDirection = (direction: TDirection, container?: HTMLElement) => {
  const sizeProperty = direction === 'horizontal' ? 'clientWidth' : 'clientHeight'
  return getDocumentElement(container)[sizeProperty]
}

const getStartPositionValue = ({
  popupData,
  direction,
  container,
}: {
  popupData: IPopupData
  direction: TDirection
  container?: HTMLElement
}) => {
  const startPositionCoordinate = popupData.startPosition[direction]
  let coordinate = popupData.targetCoords[startPositionCoordinate]
  const scroll = getScroll({ direction, position: popupData.position, container })
  if (startPositionCoordinate === 'center') {
    coordinate =
      direction === 'horizontal'
        ? popupData.targetCoords.horizontalCenter
        : popupData.targetCoords.verticalCenter
  }
  return coordinate + scroll
}

const getScroll = ({
  direction,
  position,
  container,
}: {
  direction: TDirection
  position: IPopupData['position']
  container?: HTMLElement
}) => {
  if (position === 'fixed') {
    return 0
  }
  // When popup is positioned relative to a container (e.g. shadow DOM mount point),
  // we translate coordinates using getBoundingClientRect() deltas and must not add document scroll.
  if (container) {
    return 0
  }
  const scrollProperty = direction === 'horizontal' ? 'scrollLeft' : 'scrollTop'
  return getDocumentElement()[scrollProperty]
}

const isRevertDirection = (coordinate: TPopupVertical | TPopupHorizontal) =>
  coordinate === TPopupHorizontal.Right || coordinate === TPopupVertical.Bottom
